import React, { useMemo, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import "../styles/contact-wrap.scss";

const ContactInner = () => {
  // If your API is on another domain/subdomain, set:
  // REACT_APP_API_BASE=https://api.kiubunifu.com   (no trailing slash)
  const API_BASE = (process.env.REACT_APP_API_BASE || "").replace(/\/$/, "");

  const SITEKEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY;

  const contactCaptchaRef = useRef(null);
  const feedbackCaptchaRef = useRef(null);

  // Feedback panel state
  const [showFeedback, setShowFeedback] = useState(false);
  const [experienceRating, setExperienceRating] = useState(null);
  const [recommendRating, setRecommendRating] = useState(null);
  const [feedback, setFeedback] = useState("");
  const [feedbackCaptchaToken, setFeedbackCaptchaToken] = useState(null);
  const [sendingFeedback, setSendingFeedback] = useState(false);

  // Contact form state (controlled, easier to reset reliably)
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [contactCaptchaToken, setContactCaptchaToken] = useState(null);
  const [sendingContact, setSendingContact] = useState(false);

  // ✅ Global toast (top message)
  // { type: 'success'|'error', text: string }
  const [toast, setToast] = useState(null);
  const toastTimerRef = useRef(null);

  const captchaMissing = useMemo(() => !SITEKEY, [SITEKEY]);

  const showToast = (payload, ms = 1800) => {
    // clear any previous timer
    if (toastTimerRef.current) {
      clearTimeout(toastTimerRef.current);
      toastTimerRef.current = null;
    }
    setToast(payload);
    toastTimerRef.current = setTimeout(() => {
      setToast(null);
      toastTimerRef.current = null;
    }, ms);
  };

  const postJson = async (path, body) => {
    const url = `${API_BASE}${path}`;
    const res = await fetch(url, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    });

    let data = {};
    try {
      data = await res.json();
    } catch {
      // ignore
    }

    return { res, data, url };
  };

  const resetContactCaptcha = () => {
    setContactCaptchaToken(null);
    try {
      contactCaptchaRef.current?.reset();
    } catch {
      // ignore
    }
  };

  const resetFeedbackCaptcha = () => {
    setFeedbackCaptchaToken(null);
    try {
      feedbackCaptchaRef.current?.reset();
    } catch {
      // ignore
    }
  };

  const handleFeedbackSubmit = async (e) => {
    e.preventDefault();

    if (!experienceRating || !recommendRating) {
      showToast(
        { type: "error", text: "Please rate both your experience and how likely you are to recommend us." },
        2200
      );
      return;
    }

    if (!feedbackCaptchaToken) {
      showToast({ type: "error", text: "Please complete the CAPTCHA on the feedback form." }, 2200);
      return;
    }

    try {
      setSendingFeedback(true);

      const { res, data } = await postJson("/api/feedback", {
        experienceRating,
        recommendRating,
        feedback,
        captchaToken: feedbackCaptchaToken,
      });

      if (!res.ok) {
        showToast(
          { type: "error", text: data?.message || `Failed to send feedback. (${res.status})` },
          2400
        );
        return;
      }

      // ✅ success toast
      showToast({ type: "success", text: "Feedback submitted successfully." }, 1800);

      // Reset feedback UI
      setShowFeedback(false);
      setExperienceRating(null);
      setRecommendRating(null);
      setFeedback("");
      resetFeedbackCaptcha();
    } catch (err) {
      console.error(err);
      showToast({ type: "error", text: "Network error. Please try again." }, 2400);
    } finally {
      setSendingFeedback(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();

    const safeName = (name || "").trim();
    const safeEmail = (email || "").trim();
    const safePhone = (phone || "").trim();
    const safeSubject = (subject || "").trim();
    const safeMessage = (message || "").trim();

    if (!safeName || !safeEmail || !safeMessage) {
      showToast({ type: "error", text: "Please fill in Your Name, Your Email, and Message." }, 2200);
      return;
    }

    if (!contactCaptchaToken) {
      showToast({ type: "error", text: "Please complete the CAPTCHA." }, 2200);
      return;
    }

    try {
      setSendingContact(true);

      const { res, data } = await postJson("/api/contact", {
        name: safeName,
        email: safeEmail,
        phone: safePhone,
        subject: safeSubject,
        message: safeMessage,
        captchaToken: contactCaptchaToken,
      });

      if (!res.ok) {
        showToast({ type: "error", text: data?.message || `Failed to send message. (${res.status})` }, 2400);
        return;
      }

      // ✅ success toast
      showToast({ type: "success", text: "Message submitted successfully." }, 1800);

      // Reset fields + captcha
      setName("");
      setEmail("");
      setPhone("");
      setSubject("");
      setMessage("");
      resetContactCaptcha();
    } catch (err) {
      console.error(err);
      showToast({ type: "error", text: "Network error. Please try again." }, 2400);
    } finally {
      setSendingContact(false);
    }
  };

  return (
    <>
      {/* ✅ Global toast (top) */}
      {toast && (
        <div
          style={{
            position: "fixed",
            top: 18,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 9999,
            padding: "10px 14px",
            borderRadius: 10,
            fontSize: 14,
            fontWeight: 700,
            color: toast.type === "success" ? "#0f5132" : "#842029",
            background: toast.type === "success" ? "#d1e7dd" : "#f8d7da",
            border: toast.type === "success" ? "1px solid #badbcc" : "1px solid #f5c2c7",
            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
            maxWidth: "92vw",
          }}
          role="status"
          aria-live="polite"
        >
          {toast.text}
        </div>
      )}

      {/* Page title at the very top */}
      <div>
        <div className="container">
          <div className="title-area mb-40">
            <h2 className="sec-title style2">Get In Touch</h2>
          </div>
        </div>
      </div>

      {/* Details + form row */}
      <div className="space-bottom">
        <div className="container">
          <div className="row gy-40 contact-row-equal">
            {/* LEFT: contact details */}
            <div className="col-lg-6">
              <div className="contact-details-panel">
                {/* PHONE BLOCK */}
                <div className="contact-details-block">
                  <div className="contact-details-row">
                    <div className="contact-details-icon">
                      <i className="fas fa-phone-alt" aria-hidden="true" style={{ fontSize: "21px" }} />
                    </div>
                    <div className="contact-details-text">
                      <div className="contact-details-value">+256-393-162-767</div>
                    </div>
                  </div>

                  <div className="contact-details-row">
                    <div className="contact-details-icon whatsapp">
                      <i className="fab fa-whatsapp" aria-hidden="true" />
                    </div>
                    <div className="contact-details-text">
                      <div className="contact-details-value">+256-751-003-997</div>
                    </div>
                  </div>
                </div>

                {/* LOCATION BLOCK */}
                <div className="contact-details-block">
                  <div className="contact-details-row">
                    <div className="contact-details-icon">
                      <i className="fas fa-map-marker-alt" aria-hidden="true" />
                    </div>
                    <div className="contact-details-text">
                      <div className="contact-details-value">
                        Martyrs Mall, Plot 1667, Kyaliwajjala, Namugongo
                        <br />
                        Off Kira Road, Wakiso.
                        <br />
                        P.O. Box 40028, Kampala, Uganda.
                      </div>
                    </div>
                  </div>
                </div>

                {/* EMAIL BLOCK */}
                <div className="contact-details-block">
                  <div className="contact-details-row">
                    <div className="contact-details-icon">
                      <i className="fas fa-envelope" aria-hidden="true" />
                    </div>
                    <div className="contact-details-text">
                      <div className="contact-details-value">customerservice@kiubunifu.com</div>
                    </div>
                  </div>
                </div>

                {/* FEEDBACK TEASER + INLINE PANEL */}
                <div className="feedback-teaser">
                  <h4 className="feedback-teaser-title">Leave Us Some Feedback.</h4>
                  <p className="feedback-teaser-text">Good or bad, we love to hear from you.</p>
                  <button type="button" className="feedback-open-btn" onClick={() => setShowFeedback((v) => !v)}>
                    {showFeedback ? "Close feedback" : "Send Feedback"}
                  </button>
                </div>

                {showFeedback && (
                  <form className="feedback-panel" onSubmit={handleFeedbackSubmit}>
                    {/* Question 1 */}
                    <div className="feedback-field">
                      <label className="feedback-label">
                        User Experience rating (1–10) <span className="required">*</span>
                      </label>
                      <p className="feedback-helper">On the scale of 1–10, rate your experience using this website.</p>
                      <div className="rating-scale">
                        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                          <button
                            type="button"
                            key={`exp-${n}`}
                            className={"rating-pill" + (experienceRating === n ? " active" : "")}
                            onClick={() => setExperienceRating(n)}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Question 2 */}
                    <div className="feedback-field">
                      <label className="feedback-label">
                        User Recommendation rating (1–10) <span className="required">*</span>
                      </label>
                      <p className="feedback-helper">How likely are you to recommend our website to a friend or colleague?</p>
                      <div className="rating-scale">
                        {Array.from({ length: 10 }, (_, i) => i + 1).map((n) => (
                          <button
                            type="button"
                            key={`rec-${n}`}
                            className={"rating-pill" + (recommendRating === n ? " active" : "")}
                            onClick={() => setRecommendRating(n)}
                          >
                            {n}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Feedback text */}
                    <div className="feedback-field">
                      <label className="feedback-label">Feedback message</label>
                      <textarea
                        className="feedback-textarea"
                        placeholder="Tell us about your experience…"
                        value={feedback}
                        onChange={(e) => setFeedback(e.target.value)}
                      />
                    </div>

                    {/* CAPTCHA for feedback */}
                    <div className="feedback-field">
                      {captchaMissing ? (
                        <div style={{ color: "#ffb" }}>
                          CAPTCHA key missing. Set <code>REACT_APP_RECAPTCHA_SITE_KEY</code> and rebuild.
                        </div>
                      ) : (
                        <ReCAPTCHA
                          ref={feedbackCaptchaRef}
                          sitekey={SITEKEY}
                          onChange={(t) => setFeedbackCaptchaToken(t)}
                          onExpired={() => setFeedbackCaptchaToken(null)}
                        />
                      )}
                    </div>

                    <button type="submit" className="global-btn feedback-submit-btn" disabled={sendingFeedback || captchaMissing}>
                      {sendingFeedback ? "Sending..." : "Send Feedback"}
                    </button>
                  </form>
                )}
              </div>
            </div>

            {/* RIGHT: contact form */}
            <div className="col-xl-6 col-lg-6">
              <div className="contact-form contact-form-panel">
                <form onSubmit={handleContactSubmit}>
                  <div className="row">
                    <div className="col-md-6 form-group">
                      <input
                        type="text"
                        name="name"
                        placeholder="Your Name"
                        className="form-control style-border"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                      />
                    </div>

                    <div className="col-md-6 form-group">
                      <input
                        type="text"
                        name="email"
                        placeholder="Your Email"
                        className="form-control style-border"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                      />
                    </div>

                    <div className="col-md-6 form-group">
                      <input
                        type="text"
                        name="phone"
                        placeholder="Phone Number"
                        className="form-control style-border"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                      />
                    </div>

                    <div className="col-md-6 form-group">
                      <input
                        type="text"
                        name="subject"
                        placeholder="Subject"
                        className="form-control style-border"
                        value={subject}
                        onChange={(e) => setSubject(e.target.value)}
                      />
                    </div>

                    <div className="col-12 form-group">
                      <textarea
                        name="message"
                        placeholder="Message here.."
                        className="form-control style-border"
                        value={message}
                        onChange={(e) => setMessage(e.target.value)}
                      />
                    </div>

                    {/* CAPTCHA for contact */}
                    <div className="col-12 form-group">
                      {captchaMissing ? (
                        <div style={{ color: "#ffb" }}>
                          CAPTCHA key missing. Set <code>REACT_APP_RECAPTCHA_SITE_KEY</code> and rebuild.
                        </div>
                      ) : (
                        <ReCAPTCHA
                          ref={contactCaptchaRef}
                          sitekey={SITEKEY}
                          onChange={(t) => setContactCaptchaToken(t)}
                          onExpired={() => setContactCaptchaToken(null)}
                        />
                      )}
                    </div>

                    <div className="col-12 form-group mb-0">
                      <button className="global-btn w-100" type="submit" disabled={sendingContact || captchaMissing}>
                        {sendingContact ? "Sending..." : "Send Now"}
                        <img src="assets/img/icon/right-icon.svg" alt="Bizmaster" />
                      </button>
                    </div>

                    {/* Optional tiny debug: shows which API base is being used */}
                    {/* <div className="col-12" style={{ opacity: 0.7, fontSize: 12, marginTop: 10 }}>
                      API: {API_BASE || "(same-origin)"} /api/...
                    </div> */}
                  </div>
                </form>
              </div>
            </div>
            {/* /RIGHT */}
          </div>
        </div>
      </div>
    </>
  );
};

export default ContactInner;
